import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Trash2, Edit3, Loader2, Save, X, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

const VEHICLES = ["E-Scooter", "E-Bike", "Mobility/Power Chair"];
const CATEGORIES = ["Diagnostics", "Battery", "Brakes", "Tyres", "Motor", "Charging", "Controls", "Lighting", "Service Packages", "Other"];

const blankForm = {
  sku: "", name: "", vehicle: "E-Scooter", category: "Diagnostics",
  price: "", cost: "", price_text: "", price_from: false, summary: "", active: true, sort_order: 100,
};

const AdminServices = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [filterActive, setFilterActive] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // service object or null
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/services");
      setItems(res.data);
    } catch (err) {
      if (err.response?.status === 401) { setAdminToken(null); navigate("/admin/login"); }
      else toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (filterCat && i.category !== filterCat) return false;
      if (filterActive === "active" && !i.active) return false;
      if (filterActive === "inactive" && i.active) return false;
      return true;
    });
  }, [items, filterCat, filterActive]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((s) => { (g[s.category] ||= []).push(s); });
    return g;
  }, [filtered]);

  const startCreate = () => { setEditingId(null); setForm(blankForm); setShowForm(true); };
  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      sku: s.sku, name: s.name, vehicle: s.vehicle, category: s.category,
      price: s.price ?? "", price_text: s.price_text ?? "",
      price_from: !!s.price_from, summary: s.summary, active: !!s.active, sort_order: s.sort_order ?? 100,
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(blankForm); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.summary) { toast.error("Name and summary are required"); return; }
    const payload = {
      sku: form.sku, name: form.name, vehicle: form.vehicle, category: form.category,
      price: form.price === "" ? 0 : Number(form.price),
      cost: form.cost === "" ? 0 : Number(form.cost),
      price_text: form.price_text || null,
      price_from: !!form.price_from, summary: form.summary,
      active: !!form.active, sort_order: Number(form.sort_order || 0),
    };
    setSaving(true);
    try {
      if (editingId) await api.patch(`/admin/services/${editingId}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editingId ? "Service updated" : "Service created");
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.patch(`/admin/services/${s.id}`, { active: !s.active });
      load();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  const remove = (s) => setConfirmDelete(s);

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/services/${confirmDelete.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  };

  return (
    <AdminLayout
      eyebrow="Pricing catalogue"
      title="Services"
      actions={
        <button onClick={startCreate} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-spark-yellow text-spark-ink hover:bg-spark-yellow-dark text-xs uppercase tracking-wider font-bold" data-testid="add-service-btn">
          <Plus className="size-4" /> Add service
        </button>
      }
    >
      <div className="bg-spark-floor text-spark-ink rounded-xl border border-spark-line p-4 sm:p-6" data-testid="admin-services-page">
        <main className="space-y-6">
        <div className="surface-card p-4 sm:p-5 grid sm:grid-cols-3 gap-3">
          <select className="input-style" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="filter-category">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="input-style" value={filterActive} onChange={(e) => setFilterActive(e.target.value)} data-testid="filter-active">
            <option value="">All visibility</option>
            <option value="active">Visible to customers</option>
            <option value="inactive">Hidden / disabled</option>
          </select>
          <p className="label-mono self-center sm:text-right">{filtered.length} of {items.length} services</p>
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="size-6 animate-spin inline text-spark-teal" /></div>
        ) : (
          Object.keys(grouped).length === 0 ? (
            <p className="text-center py-16 text-spark-slate" data-testid="services-empty">No services match.</p>
          ) : Object.keys(grouped).map((cat) => (
            <section key={cat} className="surface-card overflow-hidden" data-testid={`admin-cat-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center justify-between bg-spark-floor px-5 py-3 border-b border-spark-line">
                <h2 className="font-display uppercase tracking-tight text-lg font-bold">{cat}</h2>
                <span className="label-mono">{grouped[cat].length}</span>
              </div>
              <ul className="divide-y divide-spark-line">
                {grouped[cat].map((s) => (
                  <li key={s.id} className={`p-4 sm:p-5 grid sm:grid-cols-12 gap-3 items-center transition ${!s.active ? "opacity-60" : ""}`} data-testid={`service-${s.sku}`}>
                    <div className="sm:col-span-5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display uppercase font-bold text-base">{s.name}</h3>
                        <span className="label-mono text-spark-teal">{s.vehicle}</span>
                        {!s.active && <span className="label-mono text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Hidden</span>}
                      </div>
                      <p className="text-xs text-spark-slate mt-1 line-clamp-2">{s.summary}</p>
                    </div>
                    <div className="sm:col-span-2 font-mono text-xs">{s.sku}</div>
                    <div className="sm:col-span-2 font-display uppercase font-bold text-lg">
                      {s.price_text || (s.price === 0 ? "Quote" : `${s.price_from ? "From " : ""}$${Number(s.price).toFixed(2)}`)}
                    </div>
                    <div className="sm:col-span-3 flex items-center gap-1.5 sm:justify-end">
                      <button onClick={() => toggleActive(s)} className="p-2 rounded-sm border border-spark-line hover:border-spark-teal hover:text-spark-teal transition" title={s.active ? "Hide from customers" : "Show to customers"} data-testid={`toggle-${s.sku}`}>
                        {s.active ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                      </button>
                      <button onClick={() => startEdit(s)} className="p-2 rounded-sm border border-spark-line hover:border-spark-teal hover:text-spark-teal transition" title="Edit" data-testid={`edit-${s.sku}`}>
                        <Edit3 className="size-4" />
                      </button>
                      <button onClick={() => remove(s)} className="p-2 rounded-sm border border-spark-line hover:border-red-500 hover:text-red-500 transition" title="Delete" data-testid={`delete-${s.sku}`}>
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </main>

      {showForm && (
        <div className="fixed inset-0 z-40 bg-spark-teal-deep/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto" data-testid="service-form-modal">
          <form onSubmit={save} className="bg-white w-full max-w-2xl rounded-md shadow-xl border border-spark-line my-8">
            <div className="bg-spark-teal-deep text-white p-5 flex items-center justify-between rounded-t-md">
              <div>
                <p className="label-mono text-spark-yellow">{editingId ? "Edit" : "New"} service</p>
                <h2 className="font-display uppercase tracking-tight text-xl font-bold">{editingId ? form.name || "Edit service" : "Add a new service"}</h2>
              </div>
              <button type="button" onClick={closeForm} className="text-slate-300 hover:text-white" data-testid="close-form"><X className="size-5" /></button>
            </div>
            <div className="p-5 sm:p-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-mono mb-2 inline-block">SKU <span className="text-spark-slate text-[10px] font-mono normal-case tracking-normal">(optional · auto-generated if blank)</span></label>
                  <input className="input-style font-mono" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="auto · e.g. BAT-SE-0009" data-testid="form-sku" />
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Name <span className="text-spark-teal">*</span></label>
                  <input className="input-style" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Vehicle</label>
                  <select className="input-style" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} data-testid="form-vehicle">
                    {VEHICLES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Category</label>
                  <select className="input-style" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Price (NZD)</label>
                  <input type="number" min={0} step="0.01" className="input-style" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="0 = Quote" data-testid="form-price" />
                </div>
                <div>
                  <label className="label-mono mb-2 inline-block">Cost (NZD) <span className="text-spark-slate normal-case tracking-normal text-[10px]">parts/labour</span></label>
                  <input type="number" min={0} step="0.01" className="input-style" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0" data-testid="form-cost" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-mono mb-2 inline-block">Price label override</label>
                  <input className="input-style" value={form.price_text} onChange={(e) => setForm({ ...form, price_text: e.target.value })} placeholder='e.g. "From $499 + parts"' data-testid="form-price-text" />
                </div>
              </div>
              <div>
                <label className="label-mono mb-2 inline-block">Summary <span className="text-spark-teal">*</span></label>
                <textarea rows={4} className="input-style resize-none" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="form-summary" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="size-4 accent-spark-teal" data-testid="form-active" />
                  <span className="label-mono">Visible to customers</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.checked })} className="size-4 accent-spark-teal" data-testid="form-price-from" />
                  <span className="label-mono">"From" price</span>
                </label>
                <div>
                  <label className="label-mono mb-1 inline-block">Sort order</label>
                  <input type="number" className="input-style" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="form-sort" />
                </div>
              </div>
            </div>
            <div className="bg-spark-floor border-t border-spark-line p-4 flex justify-end gap-2 rounded-b-md">
              <button type="button" onClick={closeForm} className="btn-outline-ink" data-testid="cancel-form">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary" data-testid="save-service">
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} {editingId ? "Save changes" : "Create service"}
              </button>
            </div>
          </form>
        </div>
      )}
      </div>
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete this service?"
        body={confirmDelete && (
          <>
            <p>You're about to permanently delete <strong className="text-white">{confirmDelete.name}</strong> ({confirmDelete.sku}).</p>
            <p className="text-slate-400">This cannot be undone. The service will disappear from the public catalogue and the booking quote builder.</p>
          </>
        )}
        confirmLabel="Delete service"
        busy={deleting}
        onConfirm={doDelete}
        onCancel={() => !deleting && setConfirmDelete(null)}
        testId="confirm-delete-service"
      />
    </AdminLayout>
  );
};

export default AdminServices;
